import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from 'styled-system'
import Account from '../Account'
import Navigation from '../Navigation'
import NavLogo from '../NavLogo'
import PromoHeader from '../PromoHeader'
import { Link } from 'pcln-design-system'
import { Phone as PhoneIcon } from 'pcln-icons'
import usePartner from '../../hooks/usePartner'
import useHasDisplayPromo from '../../hooks/useHasDisplayPromo'

import get from 'lodash.get'

const Container = styled.header`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${props => props.height}px;
  background-color: ${props => props.backgroundColor};
  box-shadow: 0 1px 0 0 rgba(89, 106, 125, 0.5);
  z-index: 1500;
  -webkit-font-smoothing: initial;
  -moz-osx-font-smoothing: initial;
  font-size: 12.8px;
  padding: 12px ${themeGet('space.4')}px;
`
Container.displayName = 'Container'

const Content = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  max-width: 1440px;
`

const PhoneNumberLink = styled(Link)`
  min-width: fit-content;
  white-space: nowrap;
`

PhoneNumberLink.displayName = 'PhoneNumberLink'

function Header ({ activeNavLink }) {
  const shouldDisplayPromo = promo => {
    if (promo.promoNames) {
      // Should only show this promo if the associated promotions qualify (are "live")
      return useHasDisplayPromo(promo.promoNames)
    }

    return true
  }

  const getPromoToDisplay = promoConfig => {
    if (promoConfig) {
      const primaryPromo = promoConfig.primary
      const secondaryPromo = promoConfig.secondary

      if (primaryPromo && shouldDisplayPromo(primaryPromo)) {
        return primaryPromo
      }

      if (secondaryPromo && shouldDisplayPromo(secondaryPromo)) {
        return secondaryPromo
      }
    }

    return null
  }

  const headerConfig = usePartner('config.header')
  const appName = usePartner('appName', '')

  const promoConfig = get(headerConfig, 'promo')
  const promoToDisplay = getPromoToDisplay(promoConfig)
  const showPromoBanner =
    promoToDisplay &&
    promoToDisplay.acceptedApps &&
    promoToDisplay.acceptedApps.includes(appName)

  const navigation = get(headerConfig, 'navigation', [])

  const logoPath = get(headerConfig, 'logoPath', '')
  const logoHref = get(headerConfig, 'logoHref')
  const logoTitle = get(headerConfig, 'logoTitle', '')

  const secondaryLogoPath = get(headerConfig, 'secondaryLogoPath')
  const secondaryLogoHref = get(headerConfig, 'secondaryLogoHref')

  const phoneNumber = get(headerConfig, 'phoneNumber')

  const height = get(headerConfig, 'height', 60)

  const color = get(headerConfig, 'large.color', '#001833')
  const backgroundColor = get(headerConfig, 'large.backgroundColor', '#fefefe')
  const dropdownColor = get(
    headerConfig,
    'large.dropdownColor',
    backgroundColor
  )
  const linkAlignment = get(headerConfig, 'large.linkAlignment', 'center')
  const linksOpenNewTab = get(headerConfig, 'linksOpenNewTab', true)

  const linkTarget = linksOpenNewTab ? '_blank' : undefined

  return (
    <>
      {showPromoBanner && <PromoHeader {...promoToDisplay} />}
      <Container
        id='global-header'
        height={height}
        backgroundColor={backgroundColor}
      >
        <Content>
          <NavLogo
            title={logoTitle}
            path={logoPath}
            href={logoHref}
            target={linkTarget}
          />
          <Navigation
            navigation={navigation}
            target={linkTarget}
            color={color}
            dropdownColor={dropdownColor}
            align={linkAlignment}
            activeNavLink={
              activeNavLink ?? get(headerConfig, 'activeNavLink', '')
            }
          />
          {phoneNumber && (
            <>
              <PhoneIcon pr={1} />
              <PhoneNumberLink href={`tel:${phoneNumber}`} color='text.dark'>
                {phoneNumber}
              </PhoneNumberLink>
            </>
          )}
          <Account />
          {secondaryLogoPath && (
            <NavLogo
              path={secondaryLogoPath}
              href={secondaryLogoHref}
              target={linkTarget}
              color={color}
            />
          )}
        </Content>
      </Container>
    </>
  )
}

Header.displayName = 'Header'

Header.propTypes = {
  activeNavLink: PropTypes.string
}

export default memo(Header)
